import { useState } from "react";
import { Search } from "lucide-react";

import { cn, initialsOf } from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import { roomTimeLabel, type GroupRoom } from "../api/groupChat";

interface GroupRoomsPanelProps {
  rooms: GroupRoom[];
  selectedId: number | null; // selected loanId
  onSelect: (loanId: number) => void;
  className?: string;
}

/** Room list for group chat — visually identical to ConversationsPanel (search +
 *  avatar/preview/unread rows). Each row is one loan-phase room (loans.loan_id). */
export function GroupRoomsPanel({
  rooms,
  selectedId,
  onSelect,
  className,
}: GroupRoomsPanelProps) {
  const [query, setQuery] = useState("");
  const filtered = rooms.filter((r) =>
    r.title.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <aside
      className={cn(
        "min-w-0 flex-1 flex-col border-r border-white/5 md:w-[320px] md:flex-none",
        className
      )}
    >
      <div className="px-4 pb-3 pt-5">
        <h1 className="mb-4 text-xl font-bold">Grupperom</h1>
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk i grupperom"
            aria-label="Søk i grupperom"
            className="h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] pl-10 pr-3 text-sm text-white placeholder:text-white/40 focus:border-teal-400/50 focus:outline-none"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {rooms.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-white/40">
            Du deltar ikke i noen grupperom ennå. Skru på gruppe-chat i Innstillinger for å
            delta i rommene for lånene dine.
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-white/40">
            Ingen grupperom funnet.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {filtered.map((r) => (
              <li key={r.loanId}>
                <GroupRoomRow
                  room={r}
                  selected={r.loanId === selectedId}
                  onClick={() => onSelect(r.loanId)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function GroupRoomRow({
  room,
  selected,
  onClick,
}: {
  room: GroupRoom;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-white/[0.04]",
        selected &&
          "bg-teal-500/10 ring-1 ring-inset ring-teal-500/20 hover:bg-teal-500/10"
      )}
    >
      <Avatar initials={initialsOf(room.title)} className="size-12 text-base" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[15px] font-semibold text-white">{room.title}</span>
          <span className="shrink-0 text-xs text-white/40">
            {roomTimeLabel(room.lastMessageAt)}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span
            className={cn(
              "truncate text-sm",
              room.unread > 0
                ? "font-medium text-white"
                : room.preview
                  ? "text-white/55"
                  : "italic text-white/30"
            )}
          >
            {room.preview || "Ingen meldinger ennå"}
          </span>
          {room.unread > 0 && (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-teal-400 text-[11px] font-semibold text-[#04231d]">
              {room.unread}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-white/35">
          {room.optInMemberCount} {room.optInMemberCount === 1 ? "deltaker" : "deltakere"}
        </div>
      </div>
    </button>
  );
}
